import { useState } from 'react';
import {
  EuiButtonEmpty,
  EuiContextMenuItem,
  EuiContextMenuPanel,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPopover,
  EuiErrorBoundary,
} from '@elastic/eui';
import { CONSTANTS } from 'utils/constants';

/**
 * Offset is the index of where items should start to be returned from
 * based on the current pagination values.
 */
export const calculateOffset = (currentPage: number, limit: number) => {
  return currentPage * limit;
};

interface PaginationProps {
  /**
   * True if the expand/collapse all items should be set to the expanded state
   */
  allOpen?: boolean;
  /**
   * True if the user does not get to set how many items they can view
   */
  hideLimit?: boolean;
  /**
   * True if the pagination component should be disabled, such as when data is loading
   */
  isDisabled?: boolean;
  /**
   * True if the expand/collapse all items option should be displayed
   */
  showAllOpen?: boolean;
  /**
   * True if the option to navigate to the previous page should be disabled
   */
  nextPageDisabled?: boolean;
  /**
   * True if the option to navigate to the next page should be disabled
   */
  prevPageDisabled?: boolean;
  /**
   * How many items should be displayed on each page
   */
  itemLimit?: number;
  /**
   * Notified when the user wants to go to the next page
   */
  onNextPageSelect: () => void;
  /**
   * Notified when the user wants to go to the previous page
   */
  onPrevPageSelect: () => void;
  /**
   * Notified when the user requests a different item limit
   */
  setItemLimit: (itemLimit: number) => void;
  /**
   * Notified when the user requests the open status of all items being displayed to change
   */
  toggleAllOpen: (allOpen: boolean) => void;
  /**
   * The item limits that should be available for selection
   */
  itemLimits?: number[];
}

/**
 *  Pagination with selection of how many results should display per page
 */
export const Pagination = ({
  allOpen = false,
  hideLimit = false,
  isDisabled = false,
  itemLimit = CONSTANTS.QUERY_LIMIT,
  itemLimits = [...CONSTANTS.QUERY_LIMITS],
  nextPageDisabled = true,
  prevPageDisabled = true,
  onNextPageSelect,
  onPrevPageSelect,
  setItemLimit,
  showAllOpen = false,
  toggleAllOpen,
}: PaginationProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const onLimitPopoverClick = () => setIsPopoverOpen(() => !isPopoverOpen);
  const closeLimitPopover = () => setIsPopoverOpen(false);

  const button = (
    <EuiButtonEmpty
      id="pagination-rows-per-page"
      color="text"
      iconSide="right"
      iconType="arrowDown"
      onClick={onLimitPopoverClick}
      size="s"
    >
      {`Rows per page: ${itemLimit}`}
    </EuiButtonEmpty>
  );

  const limitMenuOptions = itemLimits.map((limit: number) => (
    <EuiContextMenuItem
      disabled={isDisabled}
      icon={itemLimit === limit ? 'check' : 'empty'}
      key={limit}
      onClick={() => {
        setItemLimit(limit);
        closeLimitPopover();
      }}
    >
      {`${limit} rows`}
    </EuiContextMenuItem>
  ));

  const limitSelector = (
    <EuiPopover
      button={button}
      closePopover={closeLimitPopover}
      isOpen={isPopoverOpen}
      panelPaddingSize="none"
    >
      <EuiContextMenuPanel items={limitMenuOptions} />
    </EuiPopover>
  );

  const onToggleExpandState = () => toggleAllOpen(!allOpen);

  // Disabling sets the page count and active page to default
  // so there is nothing for the user to click.
  return (
    <EuiErrorBoundary>
      <EuiFlexGroup
        alignItems="center"
        justifyContent="spaceBetween"
      >
        <EuiFlexItem grow={false}>{hideLimit ? null : limitSelector}</EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiFlexGroup>
            <EuiFlexItem grow={false}>
              <EuiButtonEmpty
                id="pagination-previous-page"
                isDisabled={prevPageDisabled}
                iconType="arrowLeft"
                size="s"
                aria-label="Previous Page"
                onClick={onPrevPageSelect}
              >
                Previous Page
              </EuiButtonEmpty>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiButtonEmpty
                id="pagination-next-page"
                isDisabled={nextPageDisabled}
                iconSide="right"
                iconType="arrowRight"
                size="s"
                aria-label="Next Page"
                onClick={onNextPageSelect}
              >
                Next Page
              </EuiButtonEmpty>
            </EuiFlexItem>
            {showAllOpen ? (
              <EuiFlexItem grow={false}>
                <EuiButtonEmpty
                  id="pagination-toggle-expansion-state"
                  iconSide="right"
                  iconType={allOpen ? 'arrowDown' : 'arrowRight'}
                  size="s"
                  onClick={onToggleExpandState}
                  aria-label={allOpen ? 'Expand All Entries' : 'Collapse All Entries'}
                >
                  {allOpen ? 'Collapse All' : 'Expand All'}
                </EuiButtonEmpty>
              </EuiFlexItem>
            ) : null}
          </EuiFlexGroup>
        </EuiFlexItem>
      </EuiFlexGroup>
    </EuiErrorBoundary>
  );
};

export default Pagination;
